import React, {JSX, useEffect, useState} from 'react';
import styles from './_application-form.module.scss';
import { Typography, Card } from '@mui/material';
import { useSelector } from 'react-redux';
import {
  createApplication, setCustomerPortalLinkId, setLocationId, setSelectedLanguageId, setTargetAssignedToIdForNewApplications, updateApplication
} from '../../../Store/Shared/actions';
import { useParams } from 'react-router';
import { AppState } from '../../../Store/rootReducer';
import LoadingIndicator from '../../Shared/LoadingIndicator/LoadingIndicator';
import {
  Application,
  InvalidCustomerInfo,
  createDefaultCustomer,
  ICustomField,
  splitCamelCaseWord,
  hasValue,
  ApplicationWithMappedCustomers,
  CustomerWithMapping,
  isValidHtml,
  ApplicationForm,
  LanguageSelector,
  languages,
  CreditAppScoringResultPage
} from '@amsanalytics/react-shared-library';
import MessageViewer from '../../Controls/MessageViewer/MessageViewer';
import { AddressValidationInfo, AddressValidationResult, emptyAddressValidationInfo } from '../../../Models/AddressValidationInfo';
import { State } from '../../../Models/State';
import { httpGetAsync, httpPostAsync } from '../../../Http/HttpClient';
import { appSettings } from '../../../index';
import { areStringsEqual, capitalizeFirstLetter, displayError, getStateByAbbreviation, isGuid } from '../../../Helpers/Utilities';
import { isNil, isNotNil } from 'ramda';
import { Button } from '@progress/kendo-react-buttons';
import ApplicationConfirmation from './ApplicationConfirmation';
import { useAppDispatch } from '../../../Store/useAppDispatch';
import { LeadSourceTrackingData } from '../../../Models/LeadSourceParameters';
import { applicationSources } from '../../../Helpers/Constants';
import {CreateApplicationFromFormResults} from "../../../Models/CreateApplicationFromFormResults";
import {shouldRedirectAutomatically, shouldRedirectToCustomerPortal} from "../../../Utils";

export interface AddressValidationProps {
  originalAddress: any,
  validatedAddressInfo: AddressValidationInfo,
  states: State[],
  onConfirm: (useSuggestedAddress: boolean) => void,
  headerAndFooterHeight: number;
  useCuratedSite: boolean;
}

// Element that displays the original address and the USPS-validation address. It asks the user if they want to use it or keep the original address.
const AddressValidation = (props: AddressValidationProps) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <h2 style={{ margin: 0 }}>Verify Address</h2>
      <p>We found a corrected address based on the information you provided. Please confirm your address below.</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-evenly' }}>
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, alignItems: 'center' }}>
          <div style={{ fontWeight: 600, marginBottom: '3px' }}>You Entered</div>
          <div style={{ textAlign: 'center', width: '100%', wordBreak: 'break-word' }}>
            {props.originalAddress.address?.trim()}<br /> {hasValue(props.originalAddress.addressLine2?.trim()) && <>{props.originalAddress.addressLine2?.trim()}<br /></>} {capitalizeFirstLetter(props.originalAddress.city?.trim())}, {props.states.find(s => s.id === props.originalAddress.stateId)?.abbreviation?.toUpperCase()}, {props.originalAddress.zip}
          </div>
          <Button
            fillMode={'outline'}
            themeColor='primary'
            style={{ textTransform: 'none', marginTop: '10px' }}
            onClick={() => props.onConfirm(false)}
          >
            Use My Entry
          </Button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, alignItems: 'center' }}>
          <div style={{ fontWeight: 600, marginBottom: '3px' }}>Suggested</div>
          <div style={{ textAlign: 'center', width: '100%', wordBreak: 'break-word' }}>
            {props.validatedAddressInfo.address1?.trim()}<br /> {hasValue(props.validatedAddressInfo.address2?.trim()) && <>{props.validatedAddressInfo.address2?.trim()}<br /></>} {props.validatedAddressInfo.city?.trim()}, {props.validatedAddressInfo.state}, {props.validatedAddressInfo.zipCode}{hasValue(props.validatedAddressInfo.zipExtension) && <>-{props.validatedAddressInfo.zipExtension}</>}
          </div>
          <Button
            fillMode={'outline'}
            themeColor='primary'
            style={{ textTransform: 'none', marginTop: '10px' }}
            onClick={() => props.onConfirm(true)}
          >
            Use Suggested
          </Button>
        </div>
      </div>
    </div>
  );
}

export const InvalidCustomerDetail = (props: { application: Application, invalidCustomers: InvalidCustomerInfo[] }) => {
  return <Card style={{ background: 'white', margin: '-4px', marginLeft: '-8px', marginRight: '-8px', padding: '10px', fontSize: '14px', maxHeight: '300px', overflowY: 'auto' }}>
    {props.invalidCustomers.map(customer => {
      const applicationCustomer = props.application.customers[customer.customerMappingId];
      let customerName = applicationCustomer?.firstName + ' ' + applicationCustomer?.lastName;
      if (!customerName || customerName.trim() === '') {
        customerName = `Customer ${customer.customerMappingId + 1}`;
      }
      return <div style={{ display: 'flex', flexDirection: 'column', marginTop: customer.customerMappingId === 0 ? 0 : 10 }}>
        <div style={{ color: '#f3f0f0', marginTop: 2, marginBottom: 2, fontWeight: 500, width: 'calc(100% - 10px)', padding: 5, border: 'solid 1px #307fbe', background: '#307fbe' }}>{customerName}'s information is incomplete.</div>
        {customer.errors.map(error => (
          <div style={{ color: '#a02a2e', marginTop: 2, marginBottom: 2, fontWeight: 500, width: 'calc(100% - 10px)', padding: 5, border: 'solid 1px red', background: '#fef3f3' }}>{error.error}</div>
        ))}
      </div>
    })}
  </Card>;
};

export interface ApplicationFormPageProps {
  headerAndFooterHeight: number;
  useCuratedSite: boolean;
}

export default function ApplicationFormPage(props: ApplicationFormPageProps) {
  const dispatch = useAppDispatch();
  const params = useParams();
  const clientId = useSelector<AppState, string | null>(appState => appState.shared.clientId);
  const locationId = useSelector<AppState, string | null>(appState => appState.shared.locationId);
  // ID of the employee we should assign the contact's opportunity to
  const targetAssignedToIdForNewApplication = useSelector<AppState, string | null>(appState => appState.shared.targetAssignedToIdForNewApplications);
  const createApplicationFromFormResults = useSelector<AppState, CreateApplicationFromFormResults | null>(appState => appState.shared.createApplicationFromFormResults);
  const applicationResultsHaveBeenReturned = createApplicationFromFormResults !== null;
  const customerPortalLinkId = useSelector<AppState, string | null>(appState => appState.shared.customerPortalLinkId);
  const customFields = useSelector<AppState, ICustomField[] | null>(appState => appState.shared.customFields);
  const [application, setApplication] = useState<ApplicationWithMappedCustomers>();
  const [submitted, setSubmitted] = useState<boolean>(false);
  const housingStatuses = useSelector((appState: AppState) => appState.shared.housingStatuses);
  const incomePeriods = useSelector((appState: AppState) => appState.shared.incomePeriods);
  const incomeTypes = useSelector((appState: AppState) => appState.shared.incomeTypes);
  const disclosure = useSelector((appState: AppState) => appState.shared.disclosure);
  const spanishDisclosure = useSelector((appState: AppState) => appState.shared.spanishDisclosure);
  const maritalStatuses = useSelector((appState: AppState) => appState.shared.maritalStatuses);
  const maximumNumberOfApplicants = useSelector((appState: AppState) => appState.shared.maximumNumberOfApplicants);
  const identificationTypes = useSelector((appState: AppState) => appState.shared.identificationTypes);
  const minimumMonthsOfAddressInformation = useSelector((appState: AppState) => appState.shared.minimumMonthsOfAddressInformation);
  const minimumMonthsOfEmploymentInformation = useSelector((appState: AppState) => appState.shared.minimumMonthsOfEmploymentInformation);
  const states = useSelector((appState: AppState) => appState.shared.states);
  // list of IDs to filter the list of applicant addresses to show in the dropdown
  const applicantAddressStateIdsToFilterBy = useSelector((appState: AppState) => appState.shared.applicantAddressStateIdsToFilterBy);
  // list of IDs to filter the list of employment addresses to show in the dropdown
  const employerAddressStateIdsToFilterBy = useSelector((appState: AppState) => appState.shared.employerAddressStateIdsToFilterBy);
  // List of invalid application customers
  const [invalidCustomers, setInvalidCustomers] = useState<InvalidCustomerInfo[]>([]);
  // Whether or not this application form is valid
  const hasErrors = invalidCustomers && invalidCustomers.length > 0;
  // whether or not to show the "USPS Validate Address" element
  const [addressValidationInfo, setAddressValidationInfo] = useState<AddressValidationInfo>({ show: false });
  // list of IDs to filter the list of driver license addresses to show in the dropdown
  const driverLicenseStateIdsToFilterBy = useSelector((appState: AppState) => appState.shared.driverLicenseStateIdsToFilterBy);
  const applicantCreditAppFormConfig = useSelector((appState: AppState) => appState.shared.creditApplicationApplicantConfiguration);
  const coApplicantCreditAppFormConfig = useSelector((appState: AppState) => appState.shared.creditApplicationCoApplicantConfiguration);
  // whether or not to validate the SSN
  const shouldValidateSsn = useSelector((appState: AppState) => appState.shared.shouldValidateSsn);
  // whether or not to validate the customer's primary address
  const shouldValidateAddress = useSelector((appState: AppState) => appState.shared.shouldValidateAddress);
  // list of relationship to applicant types
  const relationshipsToApplicant = useSelector((appState: AppState) => appState.shared.relationshipsToApplicant);
  // Privacy policy url
  const privacyPolicyUrl = useSelector<AppState, string | null>(appState => appState.shared.privacyPolicyUrl);
  // Terms and Conditions url
  const termsConditionsUrl = useSelector<AppState, string | null>(appState => appState.shared.termsConditionsUrl);
  // Language translations
  const languageTranslations = useSelector((appState: AppState) => appState.shared.languageTranslations);
  // Whether or not the user can toggle between English and Spanish
  const allowSpanishTranslation = useSelector((appState: AppState) => appState.shared.allowSpanishTranslation);
  // selected language id. 1 = English, 2 = Spanish
  const selectedLanguageId: '1' | '2' = useSelector((appState: AppState) => appState.shared.selectedLanguageId);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const leadSourceTrackingInfo: LeadSourceTrackingData = useSelector((appState: AppState) => appState.shared.leadSourceParameters);

  // sets the default application based on the client's configuration.
  const buildAndSetDefaultApplication = () => {
    setApplication(
      {
        applicationDate: new Date(),
        customers: [createDefaultCustomer(0, true, applicantCreditAppFormConfig!)].map((c: CustomerWithMapping) => {
          return {
            ...c,
            relationshipToApplicant: undefined,
          }
        }),
        applicationCustomFields: customFields?.map(cf => {
          return {
            customFieldId: cf.id
          }
        }) ?? []
      }
    );
  }

  // There are 3 scenarios where the application is set:
  // The first is when the application terms have not been accepted by the customer. This will get current application so they can accept the terms.
  // The second is to get the information from an application previously associated with the contact on a different opportunity
  // The third is where there is no current or previous application then we want to gather whatever details from the contact opportunity that can be used to prepopulate the application form.
  const getApplicationForPortalLink = async (customerPortalLinkId: string) => {
    const appForPortalLink: Response | undefined = await httpGetAsync(`Application/getForPortalLink?customerPortalLinkId=${customerPortalLinkId}`);
    // if the response is ok, and the body is not an empty object, then set the application state variable.
    if (appForPortalLink && appForPortalLink.ok) {
      // try plucking the json from the response
      const app = await appForPortalLink.json();
      // if the application is found, then set the application state variable.
      if (isNotNil(app)) {
        const newApp: ApplicationWithMappedCustomers = {
          ...app.application,
          customers: app.application.customers.map((c: CustomerWithMapping, index: number) => {
            return {
              ...c,
              relationshipToApplicant: undefined,
              mappingId: c.mappingId ?? index
            }
          }),
          customFields: isNotNil(app.applicationCustomFields)
            ? [...app.applicationCustomFields]
            : customFields ?? []
        }
        setApplication(newApp);
      } else {
        // if the application is not found, build default application
        buildAndSetDefaultApplication();
      }
    } else {
      // if the application is not found, then show an error message to the user.
      dispatch(displayError('There was an issue loading your application. Please try refreshing the page.'));
    }
  }

  useEffect(() => {
    // either the amsCode or the customer portal link id from the url parameters
    const amsCode = (params as any)?.amsCode;
    const portalLinkId = (params as any)?.customerPortalLinkId;
    const customerId = (params as any)?.customerId;
    const salesPersonId = (params as any)?.salesPersonId;
    // ensure amsCode is present and not the customer portal link id.
    if (amsCode && !portalLinkId) {
      dispatch(setLocationId(amsCode));
    }
    // Get the salesPersonId if it is present in the url parameters.
    if (salesPersonId) {
      dispatch(setTargetAssignedToIdForNewApplications(salesPersonId));
    }
    // if customer portal link id is present, then try to get the application associated with the customer portal link.
    if (portalLinkId && isGuid(portalLinkId)) {
      if (customerId && isGuid(customerId)) {
        setCustomerId(customerId);
      }
      dispatch(setCustomerPortalLinkId(portalLinkId));
      getApplicationForPortalLink(portalLinkId);
    }
  }, [params]);

  useEffect(() => {
    // customer portal link id from the url parameters
    // const amsCode = (params as any)?.amsCode;
    const portalLinkId = (params as any)?.customerPortalLinkId;
    // if the custom fields, application configuration are loaded, and the current appliaction variable is undefined, then set the application state variable.
    // ignore this if the customer portal link id is present, because the application will be set in the getApplicationForPortalLink function (along with custome fields)
    if (isNil(portalLinkId) && customFields && applicantCreditAppFormConfig && isNil(application)) {
      buildAndSetDefaultApplication();
    }
  }, [customFields, applicantCreditAppFormConfig, params, application]);

  // After submitting an app, redirect user to the appropriate page if necessary.
  useEffect(() => {    
    if (shouldRedirectToCustomerPortal(createApplicationFromFormResults, clientId, customerPortalLinkId)) {
      window.location.replace(`${window.location.protocol}//${window.location.host}/${clientId}/portal/${customerPortalLinkId}`);
    }
    // If a custom creditApplicationRedirectUrl if one exists for this client, redirect to it.
    if (shouldRedirectAutomatically(createApplicationFromFormResults)) {
      window.location.replace(createApplicationFromFormResults!.redirectUrl!);
    }
  }, [createApplicationFromFormResults, customerPortalLinkId, clientId, customerPortalLinkId]);

  const libraryLoaded = (incomePeriods.length > 0 && incomeTypes.length > 0 && states.length > 0 && maritalStatuses.length > 0 && housingStatuses.length > 0 && applicantCreditAppFormConfig !== null);

  // Returns true if the current address and the suggested address are the same.
  const isCurrentAndSuggestedAddressesEqual = (validatedAddressInfo: AddressValidationResult, customerMappingId: number): boolean => {
    const customerPrimaryAddress = application && application.customers.find(c => c.mappingId === customerMappingId)?.addresses?.find(a => a.isPrimary === true);
    return (
      areStringsEqual(customerPrimaryAddress?.address, validatedAddressInfo.address1) &&
      areStringsEqual(customerPrimaryAddress?.address2, validatedAddressInfo.address2) &&
      areStringsEqual(customerPrimaryAddress?.city, validatedAddressInfo.city) &&
      areStringsEqual(customerPrimaryAddress?.stateId?.toString(), states.find(s => s.abbreviation?.toUpperCase() === validatedAddressInfo.state?.toUpperCase())?.id?.toString()) &&
      areStringsEqual(customerPrimaryAddress?.zip?.toString(), validatedAddressInfo.zipCode?.toString())
    );
  }

  // Gets the validated address for the customer with the given mapping id
  const getValidatedAddress = async (customerMappingId: number) => {
    const customerPrimaryAddress = application && application.customers.find(c => c.mappingId === customerMappingId)?.addresses?.find(a => a.isPrimary === true);
    // get the address information to validate
    const addressToValidate: Omit<AddressValidationInfo, "show" | "customerMappingId" | "addressMappingId"> = {
      address1: customerPrimaryAddress?.address,
      address2: customerPrimaryAddress?.address2,
      city: customerPrimaryAddress?.city,
      state: states.find(s => s.id === customerPrimaryAddress?.stateId)?.abbreviation,
      zipCode: customerPrimaryAddress?.zip?.toString(),
    };
    // Post the request to validate the address and capture the updated address
    const validatedAddress: Response | undefined = await httpPostAsync(
      `${appSettings.GetWebApiBaseUri()}/AddressValidation/validate`,
      addressToValidate
    );
    return validatedAddress;
  }

  // async post method for validating the address for the customer with the given mapping id
  const validateAddress = async (customerMappingId: number) => {
    await getValidatedAddress(customerMappingId).then((response) => {
      // If ok response, then set the address validation info so that the user can confirm the address.
      // If not ok response, then do nothing.
      if (response && response.ok) {
        response.json().then((validatedAddressInfo: AddressValidationResult) => {
          if (validatedAddressInfo.hasError !== true) {
            // only set the address validation info if the suggested address is different than the original address.
            if (!isCurrentAndSuggestedAddressesEqual(validatedAddressInfo, customerMappingId)) {
              setAddressValidationInfo({ show: true, customerMappingId: customerMappingId, addressMappingId: 0, ...validatedAddressInfo });
            }
          }
        });
      }
    });
  };

  // Returns the wording for the loading indicator based on the selected language and the application state.
  const getLoadingIndicatorWording = () => {
    if (selectedLanguageId === '1') {
      return isNil(createApplicationFromFormResults?.redirectUrl) && submitted ? 'Submitting application...' : 'Loading...';
    } else {
      return isNil(createApplicationFromFormResults?.redirectUrl) && submitted ? 'Enviando la solicitud...' : 'Cargando...';
    }
  }

  const simplifiedCredApplicationLookups = {
    housingStatuses: housingStatuses.map(housingStatus => ({ id: housingStatus.id, name: splitCamelCaseWord(housingStatus.name), sortOrder: housingStatus.sortOrder, description: housingStatus.description })),
    relationshipTypes: relationshipsToApplicant,
    payPeriods: incomePeriods,
    incomeTypes: incomeTypes,
    maritalStatuses: maritalStatuses,
    identificationTypes: identificationTypes,
    addressStates: states,
    employmentStates: states,
    identificationStates: states,
    translations: languageTranslations
  };

  const onNavigateToCuratedSite = () => {
    if (hasValue(createApplicationFromFormResults?.redirectUrl)) {
      window.location.replace(createApplicationFromFormResults!.redirectUrl!);
      window.parent?.postMessage({ event: 'redirect', redirectUrl: createApplicationFromFormResults?.redirectUrl }, '*');
    }
  };

  const getApplicationConfirmationComponent = (): JSX.Element => {
    if (createApplicationFromFormResults?.redirectToCustomResultPage) {
      return (
        <CreditAppScoringResultPage
          headerText={createApplicationFromFormResults.headerText ?? ""}
          resultMessage={createApplicationFromFormResults.resultMessage?? ""}
          nextActionText={createApplicationFromFormResults.nextActionText?? ""}
          buttonText={createApplicationFromFormResults.buttonText?? ""}
          onContinue={onNavigateToCuratedSite}
          headerAndFooterHeight={props.headerAndFooterHeight}
        />
      )
    } else {
      return (
        <div style={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <ApplicationConfirmation languageId={selectedLanguageId} />
        </div>
      );
    }
  }
  
  return (
    <>
      {applicationResultsHaveBeenReturned
        ? getApplicationConfirmationComponent()
        : application && libraryLoaded && !submitted
          ? <div className={styles.applicationFormContainer}>
            {addressValidationInfo.show && 
              <div style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000 }}>
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: 'white', padding: '20px', borderRadius: '5px' }}>
                <AddressValidation
                  originalAddress={application.customers.find(c => c.mappingId === addressValidationInfo.customerMappingId)!.addresses[0]}
                  validatedAddressInfo={addressValidationInfo}
                  headerAndFooterHeight={props.headerAndFooterHeight}
                  states={states}
                  onConfirm={(useSuggestedAddress: boolean) => {
                    // get the customer to update
                    const customer = application.customers.find(c => c.mappingId === addressValidationInfo.customerMappingId);
                    // if the user wants to use the suggested address, then update the customer address
                    if (useSuggestedAddress && customer) {
                      const newAddress = {
                        ...customer.addresses.find(a => a.mappingId === addressValidationInfo.addressMappingId)!,
                        address: addressValidationInfo.address1,
                        address2: addressValidationInfo.address2,
                        city: addressValidationInfo.city,
                        stateId: hasValue(addressValidationInfo.state) ? getStateByAbbreviation(addressValidationInfo.state!, states)?.id : undefined,
                        zip: addressValidationInfo.zipCode,
                      };
                      const newApp: ApplicationWithMappedCustomers = {
                        ...application,
                        customers: application.customers.map(c => {
                          if (c.mappingId === addressValidationInfo.customerMappingId) {
                            return {
                              ...c,
                              addresses: c.addresses.map(a => {
                                if (a.mappingId === newAddress.mappingId) {
                                  return {
                                    ...newAddress
                                  };
                                }
                                return a;
                              })
                            };
                          } else {
                            return c;
                          }
                        }),
                      };
                      setApplication(newApp);
                    }
                    setAddressValidationInfo(emptyAddressValidationInfo);
                  }}
                  useCuratedSite={props.useCuratedSite}
                />
              </div>
            </div>}
            {allowSpanishTranslation && (
              <LanguageSelector
                selectedLanguageId={selectedLanguageId}
                languages={languages}
                style={{ width: 'fit-content' }}
                label={selectedLanguageId === '1' ? 'Preferred Language' : 'Idioma Preferido'}
                onSelectLanguage={(languageId: string) => {
                  // update browser tab title based on the selected language
                  document.title = languageId === '1' ? 'Customer Credit Application' : 'Solicitud de Crédito del Cliente';
                  dispatch(setSelectedLanguageId(languageId));
                }}
              />
            )}
            <ApplicationForm
              application={application}
              customerId={customerId ?? undefined}
              invalidCustomers={invalidCustomers}
              maxNumberOfApplicants={maximumNumberOfApplicants}
              minimumMonthsOfAddressInformation={minimumMonthsOfAddressInformation ?? 0}
              minimumMonthsOfEmploymentInformation={minimumMonthsOfEmploymentInformation ?? 0}
              applicantCreditAppFormConfiguration={applicantCreditAppFormConfig}
              coApplicantCreditAppFormConfiguration={coApplicantCreditAppFormConfig ?? applicantCreditAppFormConfig}
              identificationTypes={identificationTypes}
              customFields={customFields ?? []}
              housingStatuses={housingStatuses.map(housingStatus => ({ id: housingStatus.id, name: splitCamelCaseWord(housingStatus.name), sortOrder: housingStatus.sortOrder, description: housingStatus.description }))}
              incomePeriods={incomePeriods}
              incomeTypes={incomeTypes}
              maritalStatuses={maritalStatuses}
              relationshipTypes={relationshipsToApplicant}
              states={states}
              applicantAddressStateIdsToFilterBy={applicantAddressStateIdsToFilterBy}
              employerAddressStateIdsToFilterBy={employerAddressStateIdsToFilterBy}
              driverLicenseStateIdsToFilterBy={driverLicenseStateIdsToFilterBy}
              saveButtonClicked={false}
              saveProgress={false}
              selectedLanguageId={selectedLanguageId}
              languageTranslations={languageTranslations}
              shouldValidateSSN={shouldValidateSsn}
              privacyNoticeUrl={privacyPolicyUrl || ''}
              termsAndConditionsUrl={termsConditionsUrl || ''}
              onChange={(updatedApplication: ApplicationWithMappedCustomers) => {
                setApplication(updatedApplication);
              }}
              onSetErrors={(invalidCustomers?: InvalidCustomerInfo[]) => {
                setInvalidCustomers(invalidCustomers || []);
              }}
              onApplicationCompletionProgressChanged={(application: ApplicationWithMappedCustomers, stepperIndex: number) => { }}
              onAddressNextClicked={(customerMappingId: number) => {
                // If the client has validation enabled, then validate the address.
                if (shouldValidateAddress === true) {
                  validateAddress(customerMappingId);
                }
              }}
              onSubmit={() => {
                if (!hasErrors) {
                  if (clientId !== null) {
                    if (!customerId) {
                      setSubmitted(true);
                      dispatch(createApplication({
                        ...application,
                      targetAssignedToId: targetAssignedToIdForNewApplication,
                      trackingId: hasValue(leadSourceTrackingInfo.trackingData?.trackingId) 
                        ? leadSourceTrackingInfo.trackingData.trackingId 
                        : undefined, // ID reference to tracking data stored in S3
                      source: hasValue(customerPortalLinkId) ? applicationSources.poralLink : applicationSources.websiteEmbed },
                      clientId,
                      locationId,
                      props.useCuratedSite
                      ));
                    } else if (customerPortalLinkId !== null) {
                      setSubmitted(true);
                      dispatch(updateApplication({ ...application, targetAssignedToId: targetAssignedToIdForNewApplication }, clientId, customerPortalLinkId, customerId));
                    }
                  }
                }
              }}
              requireSignature={true}
              allowSubmission={invalidCustomers.length === 0 || invalidCustomers.every(c => c.errors.length === 0)}
            />
            <div style={{ display: 'flex', flexDirection: 'column', padding: '10px' }}>
              {(selectedLanguageId === '2' && spanishDisclosure && isValidHtml(spanishDisclosure))
                ? <div dangerouslySetInnerHTML={{ __html: spanishDisclosure }} />
                : disclosure && isValidHtml(disclosure)
                  ? <div dangerouslySetInnerHTML={{ __html: disclosure }} /> :
                  <Typography style={{ fontSize: '13px', fontWeight: 'bold', color: '#565656' }}>
                    {selectedLanguageId === '2' ? (spanishDisclosure ?? disclosure) : disclosure}
                  </Typography>
              }
            </div>
          </div>
          : <LoadingIndicator label={getLoadingIndicatorWording()} />
      }
      <MessageViewer />
    </>
  );
}
